// LIBS
import React, {useEffect, useMemo, useRef, useState} from "react";
import useInterval from "../hooks/useInterval";
import {Marker} from "@react-google-maps/api";
import {DatePicker, Select, Checkbox, Button, Avatar} from "antd";
import {CalendarOutlined, NodeIndexOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import moment from "moment-timezone";
// INTERNAL
import venue from "../services/venue.service";
import race from "../services/race.service";
import competitor from "../services/competitor.service";
import fusion from "../services/fusion.service";
import heats from "../services/heats.service";
import TrackerService from "../services/tracker.service";
import horseColor from "../classes/horseColor";
import {engine_broadcast} from "../classes/engine_broadcast";
// COMPONENTS
import MonitoringMap from "../components/monitoringMap";
import HeatsMapToolbar, {HeatsFitButton} from "../components/heatsMapToolbar";
import {
    CurrentRaceTrackContext,
    PositionRaceDataContext,
} from "./privateMonitoring";

const {Option} = Select;
const dateFormat = "YYYY-MM-DD";
const STALE_POSITION_DELAY = 5000; // ms : a pastille without new HPv2 data for this long is removed

export default function RaceSelection() {
    const {t} = useTranslation();

    const [selectedDate, setSelectedDate] = useState(moment());
    const [venueOptions, setVenueOptions] = useState([]);
    const [selectedVenue, setSelectedVenue] = useState(null);
    const [races, setRaces] = useState([]);                 // [{uuid, number, label, date, competitors:[]}]
    const [selected, setSelected] = useState({});           // { competitorUuid: true }
    const [loading, setLoading] = useState(false);
    const [raceTrack, setRaceTrack] = useState('');         // track content used to center the map
    const [fusions, setFusions] = useState([]);             // [{fusionId, trackers:[]}]
    const [hpv2Positions, setHpv2Positions] = useState([]);  // raw HPv2 positions by fusionId
    const lastSeenRef = useRef({});                          // fusionId -> {ts: server timestamp, seen: client time of last new data}
    const [nowTick, setNowTick] = useState(Date.now());      // periodic tick to expire stale pastilles
    const [heatsActive, setHeatsActive] = useState(false);   // HPv2 engine running (reported by the toolbar)
    const [trackerIds, setTrackerIds] = useState({});        // tracker number (num_capt) -> HPv2 tracker id

    // HPv2 commands need the tracker id, not its number
    const trackerIdOf = (num) => trackerIds['' + num];

    const loadTrackerIds = () => {
        TrackerService.getTrackersState().then(r => {
            if (r && Array.isArray(r.data)) {
                const map = {};
                r.data.forEach(tr => { map['' + tr.num_capt] = tr.id; });
                setTrackerIds(map);
            }
        });
    };
    const webSocket = useRef();
    const restoredRef = useRef(false); // true once the persisted selection has been restored

    const getTrackers = (c) => {
        if (!c || !c.fusionId) return [];
        const f = fusions.find(f => f.fusionId === c.fusionId);
        return (f && f.trackers) ? f.trackers : [];
    };

    // list of tracker ids of the selected competitors ---------------
    const selectedTrackers = useMemo(() => {
        const trackers = [];
        races.forEach(r => (r.competitors || []).forEach(c => {
            if (selected[c.uuid]) {
                getTrackers(c).forEach(tr => { if (trackers.indexOf(tr) === -1) trackers.push(tr); });
            }
        }));
        return trackers;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [races, selected, fusions]);

    // HPv2 ids of the selected trackers (what the toolbar sends to switch them on)
    const selectedTrackerIds = useMemo(() =>
        selectedTrackers
            .map(tr => trackerIds['' + tr])
            .filter(id => id !== undefined && id !== null),
        [selectedTrackers, trackerIds]);

    // fusionId -> { raceNumber, horseNumber } across all races -------
    // only the selected horses are displayed ; a fusion can be assigned to several selected horses :
    // the horse of the race with the highest number has priority
    const fusionMap = useMemo(() => {
        const map = {};
        races.forEach(r => (r.competitors || []).forEach(c => {
            if (c.fusionId === null || c.fusionId === undefined || !selected[c.uuid]) return;
            const current = map['' + c.fusionId];
            if (!current || Number(r.number) > Number(current.raceNumber)) {
                map['' + c.fusionId] = {raceNumber: r.number, horseNumber: c.number};
            }
        }));
        return map;
    }, [races, selected]);

    // only keep pastilles that received data within the last 5s
    const freshPositions = useMemo(() => {
        const now = Date.now();
        return hpv2Positions.filter(p => {
            const s = lastSeenRef.current['' + p.fusionId];
            return s && now - s.seen <= STALE_POSITION_DELAY;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hpv2Positions, nowTick]);

    // positions of the displayed pastilles (used to center the map on them)
    const markerPositions = useMemo(() =>
        freshPositions
            .filter(p => fusionMap['' + p.fusionId] && p.latitude && p.longitude)
            .map(p => ({lat: Number(p.latitude), lng: Number(p.longitude)})),
        [freshPositions, fusionMap]);

    // build the horse pastilles from HPv2 positions only ------------
    const hpv2Markers = useMemo(() => {
        // window.google can exist before the Maps library is loaded
        if (!window.google || !window.google.maps || !window.google.maps.SymbolPath) return null;
        const built = freshPositions
            .map(p => {
                const info = fusionMap['' + p.fusionId];
                if (!info) return null;                                   // fusion not part of the loaded races
                if (!p.latitude || !p.longitude) return null;
                // label eg 203 => race 2, horse 03
                const label = `${info.raceNumber}${String(info.horseNumber).padStart(2, '0')}`;
                const color = horseColor(info.horseNumber);
                return (
                    <Marker
                        key={`hpv2_${p.fusionId}`}
                        position={{lat: Number(p.latitude), lng: Number(p.longitude)}}
                        label={{text: label, color: `#${color.font}`, fontSize: '11px', fontWeight: 'bold'}}
                        icon={{
                            path: window.google.maps.SymbolPath.CIRCLE,
                            scale: 12,
                            fillColor: `#${color.color}`,
                            fillOpacity: 1,
                            strokeColor: `#${color.border}`,
                            strokeWeight: 1,
                        }}
                        />
                );
            })
            .filter(Boolean);
        return built;
    }, [freshPositions, fusionMap]);

    // next upcoming race start time ----------------------------------
    const nextStartTime = useMemo(() => {
        const now = Date.now();
        const upcoming = races
            .map(r => r.date ? new Date(r.date).getTime() : null)
            .filter(t => t && t > now)
            .sort((a, b) => a - b);
        return upcoming.length > 0 ? upcoming[0] : null;
    }, [races]);

    // load data ------------------------------------------------------
    const loadVenueOptions = (date) => {
        venue.list(date ? date.format(dateFormat) : undefined).then(result => {
            if (result && result.data) setVenueOptions(result.data);
        });
    };

    const loadRaces = (date, venueCode) => {
        if (!date || !venueCode) {
            setRaces([]);
            setRaceTrack('');
            return;
        }
        setLoading(true);
        competitor.getListByDateAndVenue(date.format(dateFormat), venueCode)
            .then(result => {
                const list = (result && result.data) ? result.data : [];
                setRaces(list);
                setSelected({});
                centerMapOnFirstRace(list);
            })
            .finally(() => setLoading(false));
    };

    // restore the persisted heats selection (date, venue, checked horses) ----
    const restoreSelection = (sel) => {
        if (!sel || !sel.date || !sel.venueCode) { restoredRef.current = true; return; }
        const date = moment(sel.date, dateFormat);
        setSelectedDate(date);
        setSelectedVenue(sel.venueCode);
        loadVenueOptions(date);
        setLoading(true);
        competitor.getListByDateAndVenue(sel.date, sel.venueCode)
            .then(result => {
                const list = (result && result.data) ? result.data : [];
                setRaces(list);
                const restored = {};
                (sel.competitors || []).forEach(uuid => { restored[uuid] = true; });
                setSelected(restored);
                centerMapOnFirstRace(list);
            })
            .finally(() => { setLoading(false); restoredRef.current = true; });
    };

    // center the map on the barycenter of the C1 track ---------------
    const centerMapOnFirstRace = (list) => {
        setRaceTrack('');
        const c1 = list.find(r => r.number === 1) || list[0];
        if (!c1) return;
        race.get(c1.uuid).then(r => {
            if (r && r.data && r.data.track && r.data.track.content) {
                setRaceTrack(r.data.track.content);
            }
        });
    };

    // handlers -------------------------------------------------------
    const dateHandler = (date) => {
        setSelectedDate(date);
        setSelectedVenue(null);
        setRaces([]);
        setSelected({});
        setRaceTrack('');
        if (date) {
            loadVenueOptions(date);
        } else {
            setVenueOptions([]);
        }
    };

    const venueHandler = (venueCode) => {
        setSelectedVenue(venueCode);
        loadRaces(selectedDate, venueCode);
    };

    // while the engine is running, switch the trackers of (de)selected horses on / off
    const switchCompetitorsTrackers = (comps, on) => {
        if (!heatsActive) return;
        comps.forEach(c => getTrackers(c).forEach(tr => {
            const id = trackerIdOf(tr);
            if (id === undefined || id === null) return; // tracker unknown by HPv2
            TrackerService.setTrackerState({id: id, state: on ? "live" : "standby"}).then();
        }));
    };

    const toggleCompetitor = (c) => {
        switchCompetitorsTrackers([c], !selected[c.uuid]);
        setSelected(prev => ({...prev, [c.uuid]: !prev[c.uuid]}));
    };

    const selectAllInRace = (race, value) => {
        switchCompetitorsTrackers(race.competitors.filter(c => !!selected[c.uuid] !== value), value);
        setSelected(prev => {
            const next = {...prev};
            race.competitors.forEach(c => { next[c.uuid] = value; });
            return next;
        });
    };

    // engine broadcast : receive raw HPv2 positions -----------------
    const wsebReceive = (self, res) => {
        if (!Array.isArray(res)) return; // ignore 'closed' and non-command frames
        const cmd = res[0];
        const data = res[1];
        if (cmd === 'hpv2_raw_positions') {
            const list = Array.isArray(data) ? data : [];
            // remember, with the client clock, when each fusion last received NEW data
            const now = Date.now();
            list.forEach(p => {
                const key = '' + p.fusionId;
                const prev = lastSeenRef.current[key];
                if (!prev || prev.ts !== p.timestamp) {
                    lastSeenRef.current[key] = {ts: p.timestamp, seen: now};
                }
            });
            setHpv2Positions(list);
        }
    };

    const connectSocket = () => {
        webSocket.current = new engine_broadcast();
        webSocket.current.set_callback(wsebReceive);
        webSocket.current.connect();
        webSocket.current.send_cmd('hpv2_raw_positions', 'start');
        window.addEventListener('beforeunload', closeSocket);
    };

    const closeSocket = () => {
        if (webSocket.current) {
            webSocket.current.send_cmd('hpv2_raw_positions', 'stop');
            webSocket.current.close();
        }
        window.removeEventListener('beforeunload', closeSocket);
    };

    // useEffect ------------------------------------------------------
    useEffect(() => {
        fusion.list().then(r => { if (r && r.data) setFusions(r.data); });
        // if the heats engine is running, go straight to the heats venue (date, venue, selected horses),
        // otherwise start from today's venues
        Promise.all([heats.getSelection(), TrackerService.getHpv2EngineState(), race.getActivatedRace()])
            .then(([s, e, a]) => {
                const sel = s && s.data;
                // the engine is also on during a normal race : heats run only when no race is active
                const raceActive = !!(a && a.data && a.data !== 'none');
                const engineRunning = !!(e && e.data && e.data.state) && !raceActive;
                if (engineRunning && sel && sel.venueCode && sel.date) {
                    restoreSelection(sel);
                } else {
                    loadVenueOptions(moment());
                    restoredRef.current = true;
                }
            })
            .catch(() => { loadVenueOptions(moment()); restoredRef.current = true; });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // persist the selection so it survives a page refresh
    useEffect(() => {
        if (!restoredRef.current) return; // don't overwrite before the initial restore
        const competitors = Object.keys(selected).filter(uuid => selected[uuid]);
        heats.setSelection({
            date: selectedDate ? selectedDate.format(dateFormat) : null,
            venueCode: selectedVenue || null,
            competitors,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected, selectedVenue, selectedDate]);

    useEffect(() => {
        if (!webSocket.current) { connectSocket(); }
        return () => { if (webSocket.current) { closeSocket(); } };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // tracker number -> id map : load on mount, retry while HPv2 hasn't answered yet
    useEffect(() => {
        loadTrackerIds();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useInterval(() => {
        if (Object.keys(trackerIds).length === 0) loadTrackerIds();
    }, 15000);

    // tick so stale pastilles disappear even when no frame is received anymore
    useInterval(() => setNowTick(Date.now()), 1000);

    // re-subscribe periodically so the feed survives socket reconnects
    useInterval(() => {
        if (webSocket.current) { webSocket.current.send_cmd('hpv2_raw_positions', 'start'); }
    }, 3000);

    // render ---------------------------------------------------------
    return (
        <PositionRaceDataContext.Provider value={{}}>
        <CurrentRaceTrackContext.Provider value={raceTrack}>
        <div className="private_page_wrapper">
            <div className="pilotage_page_wrapper">
                <div className="pilotage_panel_data">
                    <div className="header">
                        <div className="sub_header_title">
                            <span className="title">{t('Menu.RaceSelection')}</span>
                        </div>
                    </div>

                    <div className="psc_wrapper">
                        <div className="psc_row">
                            <CalendarOutlined className="psc_icon"/>
                            <DatePicker
                                className="psc_input"
                                placeholder="Date ..."
                                value={selectedDate}
                                onChange={dateHandler}
                                format={dateFormat}/>
                        </div>
                        <div className="psc_row">
                            <NodeIndexOutlined className="psc_icon"/>
                            <Select
                                className="psc_input select"
                                placeholder={t('monitoring.meeting')}
                                value={selectedVenue}
                                onChange={venueHandler}
                                allowClear>
                                {venueOptions.map(v => (
                                    <Option key={v.code} value={v.code} className="psc_race_option_label">
                                        <span className="race_number">{`${t('monitoring.meetingShortcut')}${v.meeting}`}</span>
                                        <span>{v.label}</span>
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    </div>

                    <div className="race_selection_list" style={{overflowY: 'auto', flex: 1, padding: '0 10px'}}>
                        {races.map(race => {
                            const comps = race.competitors || [];
                            const allChecked = comps.length > 0 && comps.every(c => selected[c.uuid]);
                            return (
                                <div key={race.uuid} className="race_selection_race" style={{marginBottom: 16}}>
                                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #444'}}>
                                        <span style={{fontWeight: 'bold'}}>
                                            {`${t('monitoring.raceShortcut')}${race.number}`}
                                            {race.date ? ` - ${new Date(race.date).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}` : ''}
                                            {race.label ? ` - ${race.label}` : ''}
                                        </span>
                                        <span>
                                            <Button size="small" type="link" onClick={() => selectAllInRace(race, true)}>all</Button>
                                            <Button size="small" type="link" onClick={() => selectAllInRace(race, false)} disabled={!allChecked && comps.every(c => !selected[c.uuid])}>none</Button>
                                        </span>
                                    </div>
                                    <div style={{display: 'flex', flexWrap: 'wrap', gap: 8, paddingTop: 6}}>
                                        {comps.map(c => {
                                            const color = horseColor(c.number);
                                            return (
                                                <div key={c.uuid} style={{display: 'flex', alignItems: 'flex-start', gap: 6, padding: '4px 6px', borderRadius: 4, background: '#f5f5f5', width: 100}}>
                                                    <Checkbox
                                                        checked={!!selected[c.uuid]}
                                                        onChange={() => toggleCompetitor(c)}/>
                                                    <Avatar
                                                        style={{
                                                            backgroundColor: `#${color.color}`,
                                                            color: `#${color.font}`,
                                                            border: `1px solid #${color.border}`,
                                                            verticalAlign: 'middle',
                                                            flexShrink: 0,
                                                        }}
                                                        size={22}>
                                                        {c.number}
                                                    </Avatar>
                                                    <div style={{display: 'flex', flexDirection: 'column', fontSize: 10, lineHeight: 1.2, color: '#888'}}>
                                                        {getTrackers(c).length > 0
                                                            ? getTrackers(c).map((tr, idx) => <span key={idx}>{tr}</span>)
                                                            : <span>-</span>}
                                                    </div>
                                                    <span style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{c.name}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                        {!loading && selectedVenue && races.length === 0 &&
                            <div style={{padding: 10, color: '#888'}}>{t('monitoring.empty')}</div>}
                    </div>
                </div>
                <div className="pilotage_panel_map">
                    <MonitoringMap hideControls hideTrack>
                        {hpv2Markers}
                        <HeatsFitButton positions={markerPositions}/>
                    </MonitoringMap>
                    <HeatsMapToolbar
                        nextStartTime={nextStartTime}
                        selectedTrackers={selectedTrackerIds}
                        onHeatsActiveChange={setHeatsActive}
                        hasVenue={!!selectedVenue}/>
                </div>
            </div>
        </div>
        </CurrentRaceTrackContext.Provider>
        </PositionRaceDataContext.Provider>
    );
}
